/**
 * Reason codes on the auth 401s (`/auth/login`, `/auth/me`, `/auth/refresh`).
 *
 * `code` is stable and safe to branch on; the `error` prose beside it may be
 * reworded. The field is omitted rather than null on responses that carry no
 * code, so an absent value means "some other 401" and keeps the generic
 * handling.
 */
export type AuthFailureCode =
  /** The account was switched off. Retrying will not help — sign the user out. */
  | "ACCOUNT_INACTIVE"
  /** Bad signature, expired or revoked token. End the session quietly. */
  | "TOKEN_INVALID"
  /** No bearer header at all. */
  | "MISSING_TOKEN"
  /** Wrong credentials, or the wrong portal for this account. Stay on the form. */
  | "AUTH_FAILED";

const KNOWN_CODES = new Set<AuthFailureCode>([
  "ACCOUNT_INACTIVE",
  "TOKEN_INVALID",
  "MISSING_TOKEN",
  "AUTH_FAILED",
]);

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === "object" ? (value as Record<string, unknown>) : null;
}

/**
 * The reason code on an auth failure, or undefined for anything else. Accepts
 * either the error envelope (RTK Query `{ status, data }`, axios `{ response:
 * { data } }`) or the response body itself.
 */
export function authFailureCode(error: unknown): AuthFailureCode | undefined {
  const err = asRecord(error);
  const body = asRecord(err?.data) ?? asRecord(asRecord(err?.response)?.data) ?? err;
  const code = body?.code;
  return typeof code === "string" && KNOWN_CODES.has(code as AuthFailureCode)
    ? (code as AuthFailureCode)
    : undefined;
}

/** Whether this failure means the account itself has been switched off. */
export function isAccountInactive(error: unknown): boolean {
  return authFailureCode(error) === "ACCOUNT_INACTIVE";
}
